import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const ClearChatConfirmation = ({
  isOpen,
  onConfirm,
  onCancel,
  title = "Clear Chat History",
  message = "Are you sure you want to clear all chat history?",
  description = "This action will permanently delete all your conversation history and cannot be undone.",
  confirmLabel = "Yes, I confirm to delete all my chat history permanently",
  buttonText = "Delete"
}) => {
  const [isClearing, setIsClearing] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Reset confirmation state when popup opens
  useEffect(() => {
    if (isOpen) {
      setIsConfirmed(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsClearing(true);
    try {
      await onConfirm();
    } finally {
      setIsClearing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center clear-chat-modal">
      <div
        className="w-full max-w-sm mx-4 rounded-xl backdrop-blur-md p-3 sm:p-4"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          backgroundColor: 'var(--glass-modal-bg)',
          boxShadow: 'var(--shadow-box)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
              {title}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1 rounded-lg transition-colors"
            style={{ 
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            disabled={isClearing}
          >
            <FaTimes size={12} />
          </button>
        </div>

        {/* Warning Message */}
        <div className="mb-3">
          <p className="text-sm mb-2" style={{ color: 'var(--text-primary)' }}>
            {message}
          </p>
          <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
            {description}
          </p>
          
          {/* Confirmation Checkbox */}
          <div className="flex items-start p-2 rounded-lg" style={{ backgroundColor: 'var(--glass-input-bg)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            <input
              type="checkbox"
              id="confirm-clear"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="mr-2 w-3 h-3 mt-0.5 rounded transition-colors"
              style={{
                accentColor: '#dc2626'
              }}
              disabled={isClearing}
            />
            <label
              htmlFor="confirm-clear"
              className="text-xs cursor-pointer select-none leading-snug"
              style={{ color: 'var(--text-primary)' }}
            >
              {confirmLabel}
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-1.5 px-2 rounded-md transition-all duration-200 text-xs"
            style={{
              backgroundColor: 'var(--glass-input-bg)',
              color: 'var(--text-primary)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--glass-input-bg)';
            }}
            disabled={isClearing}
          >
            Cancel
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={isClearing || !isConfirmed}
            className="flex-1 py-1.5 px-2 rounded-md transition-all duration-200 text-white text-xs"
            style={{
              backgroundColor: (isClearing || !isConfirmed) ? '#9ca3af' : '#dc2626',
              opacity: (isClearing || !isConfirmed) ? 0.6 : 1,
              cursor: (isClearing || !isConfirmed) ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isClearing && isConfirmed) {
                e.currentTarget.style.backgroundColor = '#b91c1c';
              }
            }}
            onMouseLeave={(e) => {
              if (!isClearing && isConfirmed) {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }
            }}
          >
            {isClearing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-1"></div>
                Deleting...
              </div>
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClearChatConfirmation;